import React, { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { SharedNavbar } from '@/components/SharedNavbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PageTransition } from '@/components/PageTransition';
import {
  Plus,
  Search,
  MoreVertical,
  Trash2,
  Eye,
  CheckSquare,
  Clock,
  CheckCircle2,
  Settings,
  ArrowLeft,
  RefreshCw,
  User,
} from 'lucide-react';
import { TOOL_REGISTRY as toolConfigs } from '@/config/tools';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { useChecklistsData } from '@/hooks/useChecklistsData';

export default function ChecklistsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { userProfile, company } = useAuth();
  usePageTitle('Checklists');

  // Local UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [showAssignedToMe, setShowAssignedToMe] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedTemplateForCreate, setSelectedTemplateForCreate] = useState(null);
  const [newChecklistName, setNewChecklistName] = useState('');

  // Determine scope
  const isProjectLevel = !!projectId;

  // Use optimized hook for data fetching
  const {
    checklists,
    templates,
    space,
    loading,
    refetch: refetchChecklists,
    removeChecklistLocally
  } = useChecklistsData(company?.id, userProfile?.id, {
    projectId,
    isProjectLevel,
    search: searchQuery,
    showCompleted,
    showAssignedToMe,
    enabled: !!company?.id
  });

  // Derive spaceTools from space data
  const spaceTools = useMemo(() => {
    if (!space?.tools) return [];
    const toolsObj = typeof space.tools === 'string' ? JSON.parse(space.tools) : space.tools;
    return Object.entries(toolsObj)
      .filter(([, enabled]) => enabled === true)
      .map(([toolKey]) => ({
        id: toolKey,
        tool_type: toolKey,
        name: toolConfigs[toolKey]?.name || toolKey,
        icon: toolConfigs[toolKey]?.icon,
        color: toolConfigs[toolKey]?.color
      }));
  }, [space?.tools]);

  // Create checklist from template
  const createChecklist = useCallback(async () => {
    if (!selectedTemplateForCreate) {
      toast.error('Please select a template');
      return;
    }

    if (!newChecklistName.trim()) {
      toast.error('Please enter a checklist name');
      return;
    }

    try {
      // Create the checklist
      const { data: newChecklist, error: checklistError } = await supabase
        .from('checklists')
        .insert([{
          template_id: selectedTemplateForCreate.id,
          name: newChecklistName.trim(),
          company_id: company.id,
          project_id: isProjectLevel ? projectId : null,
          created_by: userProfile.id,
        }])
        .select()
        .single();

      if (checklistError) throw checklistError;

      // Fetch template items
      const { data: templateItems, error: itemsError } = await supabase
        .from('checklist_template_items')
        .select('*')
        .eq('template_id', selectedTemplateForCreate.id)
        .order('order_index');

      if (itemsError) throw itemsError;

      // Copy items to the new checklist
      if (templateItems && templateItems.length > 0) {
        const checklistItems = templateItems.map(item => ({
          checklist_id: newChecklist.id,
          template_item_id: item.id,
          title: item.title,
          description: item.description,
          order_index: item.order_index,
        }));

        const { error: copyError } = await supabase
          .from('checklist_items')
          .insert(checklistItems);

        if (copyError) throw copyError;
      }

      // Refresh checklists
      await refetchChecklists();

      // Reset form
      setNewChecklistName('');
      setSelectedTemplateForCreate(null);
      setIsCreateDialogOpen(false);

      toast.success('Checklist created!');

      // Navigate to the new checklist
      navigate(`/checklist/${newChecklist.id}`);
    } catch (error) {
      console.error('Error creating checklist:', error);
      toast.error('Failed to create checklist');
    }
  }, [selectedTemplateForCreate, newChecklistName, company?.id, isProjectLevel, projectId, userProfile?.id, refetchChecklists, navigate]);

  // Delete checklist
  const deleteChecklist = useCallback(async (checklistId) => {
    if (!confirm('Are you sure you want to delete this checklist? This cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('checklists')
        .delete()
        .eq('id', checklistId);

      if (error) throw error;

      removeChecklistLocally(checklistId);
      toast.success('Checklist deleted');
    } catch (error) {
      console.error('Error deleting checklist:', error);
      toast.error('Failed to delete checklist');
    }
  }, [removeChecklistLocally]);

  // View checklist
  const viewChecklist = useCallback((checklistId) => {
    navigate(`/checklist/${checklistId}`);
  }, [navigate]);

  if (loading && checklists.length === 0) {
    return (
      <AppShell3 navbar={<SharedNavbar />} sidebar={null}>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  return (
    <PageTransition>
      <AppShell3 navbar={<SharedNavbar />} sidebar={null}>
        <div className="flex flex-col h-full">
          <ToolHeader
            spaceName={space?.name}
            spaceImage={space?.avatar}
            toolName="Checklists"
            currentTool="checklists"
            spaceTools={spaceTools}
            actions={
              !loading && (
                <div className="flex items-center gap-2">
                  {/* Templates Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(isProjectLevel ? `/space/${projectId}/checklist-templates` : '/checklist-templates')}
                    className="h-8 text-sm"
                  >
                    <Settings className="h-3.5 w-3.5 mr-1.5" />
                    Templates
                  </Button>

                  {/* Search */}
                  <div className="relative w-[300px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <Input
                      placeholder="Search by checklist name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-8 text-sm"
                    />
                  </div>

                  {/* Show Completed Toggle */}
                  <Button
                    variant={showCompleted ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setShowCompleted(!showCompleted)}
                    className="h-8 text-sm"
                  >
                    {showCompleted ? 'Hide' : 'Show'} Completed
                  </Button>

                  {/* Assigned to Me Toggle */}
                  <Button
                    variant={showAssignedToMe ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setShowAssignedToMe(!showAssignedToMe)}
                    className="h-8 text-sm"
                  >
                    <User className="h-3.5 w-3.5 mr-1.5" />
                    {showAssignedToMe ? 'All' : 'Assigned to Me'}
                  </Button>

                  {/* Refresh */}
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={refetchChecklists}
                    className="h-8 w-8"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>

                  {/* New Checklist */}
                  <Button
                    size="sm"
                    onClick={() => setIsCreateDialogOpen(true)}
                    className="h-8 text-sm"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" />
                    New Checklist
                  </Button>
                </div>
              )
            }
          />

          <div className="container mx-auto px-6 py-6">
            {/* Create Checklist Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Checklist</DialogTitle>
                  <DialogDescription>
                    Create a new checklist from a template
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Select Template</label>
                    <Select
                      value={selectedTemplateForCreate?.id || ''}
                      onValueChange={(value) => {
                        const template = templates.find(t => t.id === value);
                        setSelectedTemplateForCreate(template);
                        // Auto-populate name with template name
                        if (template) {
                          setNewChecklistName(template.name);
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a template" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Checklist Name</label>
                    <Input
                      placeholder="e.g., IT Onboarding - John Doe"
                      value={newChecklistName}
                      onChange={(e) => setNewChecklistName(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Customize the name to identify this specific checklist
                    </p>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={createChecklist} className="flex-1">Create Checklist</Button>
                    <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Checklists Table */}
            {checklists.length === 0 ? (
              <div className="text-center py-16">
                <CheckSquare className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-semibold mb-2">No checklists found</h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery
                    ? 'Try adjusting your search'
                    : 'Create your first checklist to get started'}
                </p>
                {!searchQuery && (
                  <Button onClick={() => setIsCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Checklist
                  </Button>
                )}
              </div>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {checklists.map((checklist) => (
                      <TableRow
                        key={checklist.id}
                        className="cursor-pointer"
                        onClick={() => viewChecklist(checklist.id)}
                      >
                        <TableCell className="font-medium">
                          {checklist.name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {checklist.template?.name || 'No Template'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 w-[200px]">
                            <Progress value={checklist.progress} className="flex-1 h-2" />
                            <span className="text-xs text-muted-foreground whitespace-nowrap min-w-[45px]">
                              {checklist.completedItems}/{checklist.totalItems}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {checklist.is_completed ? (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Complete
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
                              <Clock className="h-3 w-3 mr-1" />
                              In Progress
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(checklist.created_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); viewChecklist(checklist.id); }}>
                                <Eye className="h-4 w-4 mr-2" />
                                View
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => { e.stopPropagation(); deleteChecklist(checklist.id); }}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        </div>
      </AppShell3>
    </PageTransition>
  );
}
